import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';

const TYPE_ICONS = {
  BOOKING_NEW: '📋',
  BOOKING_ACCEPTED: '✅',
  BOOKING_COMPLETED: '🎉',
  MESSAGE_NEW: '💬',
  REVIEW_NEW: '⭐',
};

function timeAgo(dateStr) {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString('en-AE', { day: 'numeric', month: 'short' });
}

function SkeletonRow() {
  return (
    <div className="flex items-start gap-3 px-4 py-3.5 animate-pulse">
      <div className="w-10 h-10 rounded-2xl bg-dark-surface flex-shrink-0" />
      <div className="flex-1">
        <div className="h-3.5 bg-dark-surface rounded-lg w-2/3 mb-2" />
        <div className="h-3 bg-dark-surface rounded-lg w-full mb-1" />
        <div className="h-3 bg-dark-surface rounded-lg w-1/3" />
      </div>
    </div>
  );
}

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/notifications');
      setNotifications(Array.isArray(data) ? data : (data.notifications || []));
    } catch {
      setNotifications([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const markRead = async (notif) => {
    if (notif.isRead) return;
    try {
      await api.patch(`/notifications/${notif.id}/read`);
      setNotifications((prev) =>
        prev.map((n) => n.id === notif.id ? { ...n, read: true } : n)
      );
    } catch {}
  };

  const markAllRead = async () => {
    setMarkingAll(true);
    try {
      await api.patch('/notifications/read-all');
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch {
      // fallback: mark each individually
      const unread = notifications.filter((n) => !n.isRead);
      await Promise.allSettled(unread.map((n) => api.patch(`/notifications/${n.id}/read`)));
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    }
    setMarkingAll(false);
  };

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <div className="min-h-screen bg-dark-bg pb-24">
      {/* Header */}
      <div className="px-4 pt-12 pb-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <h1 className="text-2xl font-bold text-dark-text" style={{ letterSpacing: '-0.5px' }}>
            Notifications
          </h1>
          {unreadCount > 0 && (
            <span className="min-w-[22px] h-[22px] px-1.5 bg-brand-gradient rounded-full flex items-center justify-center text-white text-xs font-bold">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            disabled={markingAll}
            className="text-brand-purple text-sm font-medium disabled:opacity-50"
          >
            {markingAll ? 'Marking...' : 'Mark all read'}
          </button>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex flex-col">
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
        </div>
      ) : notifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center pt-24 px-6">
          <div className="text-5xl mb-4">🔔</div>
          <p className="text-dark-text font-semibold text-lg mb-1">No notifications yet</p>
          <p className="text-dark-subtext text-sm text-center">
            When you get new bookings, messages, or reviews, they'll show up here.
          </p>
        </div>
      ) : (
        <div className="flex flex-col">
          {notifications.map((notif) => (
            <button
              key={notif.id}
              onClick={() => markRead(notif)}
              className={`w-full flex items-start gap-3 px-4 py-3.5 text-left border-b border-dark-border transition-colors ${
                notif.isRead ? '' : 'border-l-2 border-l-brand-purple'
              }`}
              style={!notif.isRead ? { paddingLeft: '14px' } : {}}
            >
              {/* Icon bubble */}
              <div className={`w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 text-lg ${
                notif.isRead ? 'bg-dark-surface' : 'bg-dark-surface border border-brand-purple/30'
              }`}>
                {TYPE_ICONS[notif.type] || '🔔'}
              </div>

              {/* Text */}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold leading-snug truncate ${
                  notif.isRead ? 'text-dark-subtext' : 'text-dark-text'
                }`}>
                  {notif.title}
                </p>
                {notif.body && (
                  <p className="text-dark-subtext text-xs mt-0.5 line-clamp-2 leading-relaxed">
                    {notif.body}
                  </p>
                )}
                <p className="text-dark-subtext/60 text-xs mt-1">
                  {notif.createdAt ? timeAgo(notif.createdAt) : ''}
                </p>
              </div>

              {/* Unread dot */}
              {!notif.isRead && (
                <div className="w-2 h-2 rounded-full bg-brand-purple mt-2 flex-shrink-0" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
